import { useParams } from "react-router";

import { useQuery } from "@tanstack/react-query";

import { SATOSPortal } from "@/DataAdapter/SATOSPortal";

import { ChecklistVersionsComparison } from "@/model/Procedure/ChecklistVersionsComparison";
import { ProcedureNodeStepComment } from "@/model/Procedure/ProcedureNodeStepComment";
import { ProcedureNodeSteps } from "@/model/Procedure/ProcedureNodeSteps";

import { RouteParams } from "@/service/Utilities";

const getChecklist = async (procedureId: number, checklistId: number) => {
	const response = await fetch(
		`/api/checklist/${procedureId}/${checklistId}/getChecklist`,
	);

	if (!response.ok) {
		throw new Error("Could not fetch checklist");
	}

	return response.json();
};

export const useFetchChecklist = (checklistId: number) => {
	const { id } = useParams<RouteParams>();
	const { data, isLoading, isError, error } = useQuery({
		queryKey: [ChecklistQueryKeys.checklist, +checklistId],
		queryFn: () => getChecklist(+id, +checklistId),
		enabled: !!checklistId,
		staleTime: 60000, // Data won't be considered stale for 1 minute
	});

	return { data, isLoading, isError, error };
};

const useFetchEditorContent = async (
	id: string,
	checklistId: number,
): Promise<ProcedureNodeSteps> => {
	const checklist = await getChecklist(+id, checklistId);
	// Initialize with an empty string or some default value
	return checklist;
};

export const useEditorContent = (checklistId: number | undefined) => {
	const { id } = useParams<RouteParams>();

	return useQuery({
		queryKey: [ChecklistQueryKeys.checklistPreview, +checklistId],
		queryFn: () => useFetchEditorContent(id, checklistId),
		staleTime: 60000, // Content won't go stale unless explicitly invalidated
		enabled: !!checklistId && !!id,
	});
};

export const useFetchCommentsByChecklistId = (checklistId: number) => {
	let { id } = useParams<RouteParams>();
	const procedureId = parseInt(id);
	return useQuery<Record<number, ProcedureNodeStepComment[]>, any>({
		queryKey: [
			ChecklistQueryKeys.checklist,
			checklistId,
			ChecklistQueryKeys.comments,
		],
		queryFn: () =>
			SATOSPortal.GetCommentsByChecklistId(procedureId, checklistId),
		enabled: Boolean(checklistId && procedureId),
	});
};

export const useFetchChecklistVersionsComparison = (
	checklistId: number,
	nodeId: number,
	procedureId: number,
	isActive: boolean,
) => {
	return useQuery<ChecklistVersionsComparison, any>({
		queryKey: [ChecklistQueryKeys.versionsComparison, procedureId, nodeId, checklistId],
		queryFn: () =>
			SATOSPortal.GetChecklistVersionsComparison(
				checklistId,
				nodeId,
				procedureId,
			),
			enabled: isActive,
	});
};

export const ChecklistQueryKeys = {
	checklist: "checklist",
	checklistPreview: "editorContent",
	comments: "comments",
	versionsComparison: "versionsComparison",
};
